/**
 * Composant racine de l'application.
 *
 * STRATÉGIE DE CODE-SPLITTING :
 * - Pages publiques (HomePage, ServicesPage, Legal) : import statique
 *   → sur le chemin critique, doivent être disponibles immédiatement.
 * - Pages admin : React.lazy() + <Suspense>
 *   → chargées à la demande, seulement quand l'utilisateur navigue vers /admin.
 *   → réduit le bundle initial de ~60-70% en excluant tout le code admin.
 */

import { lazy, Suspense } from 'react';
import { Routes, Route, Navigate } from 'react-router-dom';
import { appContainer, adminLoadingFallback } from './app.css';

// ================================
// COMPOSANTS PARTAGÉS (chemin critique)
// ================================
import CookieBanner from '../features/shared/components/CookieBanner/CookieBanner';
import { AdminProvider } from '../contexts';
import ApiErrorHandlerInit from './ApiErrorHandlerInit';

// ================================
// PAGES PUBLIQUES — import statique (chemin critique)
// ================================
import HomePage from '../features/portfolio/pages/Home/HomePage';
import ServicesPage from '../features/portfolio/pages/Services/ServicesPage';
import PrivacyPolicy from '../features/shared/pages/Legal/PrivacyPolicy';
import LegalNotice from '../features/shared/pages/Legal/LegalNotice';

// ================================
// PAGES D'ERREUR — import statique (nécessaire même sans JS chargé)
// ================================
import {
  ErrorBoundary,
  ErrorPagesRouter,
  NotFoundPage,
  ForbiddenPage,
  ServerErrorPage,
  GenericErrorPage,
} from '../features/shared/pages/ErrorPages';

// ================================
// PAGES ADMIN — lazy (chargées à la demande sur /admin/*)
// Chaque import dynamique crée un chunk séparé dans le bundle.
// ================================
const AdminLogin = lazy(() => import('../features/admin/pages/Login/Login'));
const AdminDashboard = lazy(() => import('../features/admin/pages/Dashboard/Dashboard'));
const AdminProjects = lazy(() => import('../features/admin/pages/Projects/ProjectsPage'));
const AdminSkills = lazy(() => import('../features/admin/pages/Skills/SkillsPage'));
const AdminAbout = lazy(() => import('../features/admin/pages/About/AboutPage'));
const AdminEducation = lazy(() => import('../features/admin/pages/Education/EducationPage'));
const AdminExperiences = lazy(() => import('../features/admin/pages/Experiences/ExperiencesPage'));
const AdminHobbies = lazy(() => import('../features/admin/pages/Hobbies/HobbiesPage'));
const AdminSettings = lazy(() => import('../features/admin/pages/Settings/SettingsPage'));
const AdminServices = lazy(() => import('../features/admin/pages/Services/ServicesPage'));
const AdminSections = lazy(() => import('../features/admin/pages/Sections/SectionPage'));
const AdminQuotes = lazy(() => import('../features/admin/pages/LifeQuotes/LifeQuotePage'));

// Composants admin partagés (layout + guards)
import AdminOnlyRoute from '../features/admin/components/AdminOnlyRoute';
import AdminLayout from '../features/admin/layouts/AdminLayout/AdminLayout';
import { ProtectedRoute } from '../features/admin/components/ProtectedRoute';

/**
 * Indicateur de chargement affiché pendant le chargement lazy des pages admin.
 * Simple spinner minimaliste pour ne pas dépendre de composants lourds.
 */
function AdminLoadingFallback(): JSX.Element {
  return (
    <div className={adminLoadingFallback}>
      Chargement...
    </div>
  );
}

/**
 * Composant principal de l'application.
 * Définit la structure de routage complète : pages publiques, légales, admin, erreurs.
 */
function App(): JSX.Element {
  return (
    <ErrorBoundary>
      <div className={appContainer}>
        <ApiErrorHandlerInit />
        <CookieBanner />
        <Routes>
          {/* Routes publiques du portfolio */}
          <Route path="/" element={<HomePage />} />
          <Route path="/portfolio" element={<HomePage />} />
          <Route path="/services" element={<ServicesPage />} />
          <Route path="/home" element={<Navigate to="/" replace />} />

          {/* Routes légales */}
          <Route path="/legal/privacy" element={<PrivacyPolicy />} />
          <Route path="/legal/terms" element={<LegalNotice />} />
          <Route path="/privacy" element={<Navigate to="/legal/privacy" replace />} />
          <Route path="/terms" element={<Navigate to="/legal/terms" replace />} />

          {/* Routes de pages d'erreur */}
          <Route path="/error/*" element={<ErrorPagesRouter />} />
          <Route path="/404" element={<NotFoundPage />} />
          <Route path="/403" element={<ForbiddenPage />} />
          <Route path="/500" element={<ServerErrorPage />} />
          <Route path="/forbidden" element={<ForbiddenPage />} />
          <Route path="/not-found" element={<NotFoundPage />} />
          <Route path="/server-error" element={<ServerErrorPage />} />
          <Route path="/maintenance" element={
            <GenericErrorPage
              statusCode={503}
              title="Maintenance en Cours"
              message="Le site est actuellement en maintenance. Nous serons de retour bientôt."
              icon="🔧"
              showRetry={false}
            />
          } />

          {/* Routes admin — toutes les pages sont chargées lazily via <Suspense> */}
          <Route
            path="/admin/*"
            element={
              <AdminProvider>
                <Suspense fallback={<AdminLoadingFallback />}>
                  <Routes>
                    {/* Page de login admin (accessible sans authentification) */}
                    <Route path="login" element={<AdminLogin />} />

                    {/* Routes admin protégées — nécessitent d'être connecté */}
                    <Route
                      path="/*"
                      element={
                        <ProtectedRoute>
                          <AdminLayout>
                            <Routes>
                              <Route path="dashboard" element={<AdminDashboard />} />
                              <Route
                                path="projects"
                                element={
                                  <AdminOnlyRoute>
                                    <AdminProjects />
                                  </AdminOnlyRoute>
                                }
                              />
                              <Route path="skills" element={<AdminSkills />} />
                              <Route path="about" element={<AdminAbout />} />
                              <Route path="education" element={<AdminEducation />} />
                              <Route path="experiences" element={<AdminExperiences />} />
                              <Route path="hobbies" element={<AdminHobbies />} />
                              <Route
                                path="settings"
                                element={
                                  <AdminOnlyRoute>
                                    <AdminSettings />
                                  </AdminOnlyRoute>
                                }
                              />
                              <Route path="services" element={<AdminServices />} />
                              <Route path="sections" element={<AdminSections />} />
                              <Route path="life-quotes" element={<AdminQuotes />} />

                              {/* Alias de routes admin */}
                              <Route path="lifequotes" element={<Navigate to="life-quotes" replace />} />
                              <Route path="quotes" element={<Navigate to="life-quotes" replace />} />
                              <Route path="citations" element={<Navigate to="life-quotes" replace />} />

                              {/* Gestion des erreurs admin */}
                              <Route path="error/*" element={<ErrorPagesRouter />} />

                              {/* Redirection par défaut vers le dashboard */}
                              <Route path="" element={<Navigate to="dashboard" replace />} />

                              {/* 404 admin */}
                              <Route path="*" element={<NotFoundPage />} />
                            </Routes>
                          </AdminLayout>
                        </ProtectedRoute>
                      }
                    />
                  </Routes>
                </Suspense>
              </AdminProvider>
            }
          />

          {/* Route de test API (développement uniquement) */}
          {import.meta.env.DEV && (
            <Route path="/api-test" element={
              <GenericErrorPage
                statusCode={200}
                title="API Test Page"
                message="Cette page est disponible uniquement en développement pour tester les connexions API."
                icon="⚙️"
                showRetry={false}
              />
            } />
          )}

          {/* Route attrape-tout — doit être en dernier */}
          <Route path="*" element={<NotFoundPage />} />
        </Routes>
      </div>
    </ErrorBoundary>
  );
}

export default App;